import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import LiveModal from 'live/components/ui/Modal'
import { Sheet } from '../../../common/types/api/excel'
import GeneralTextInput from '../../../common/components/GeneralTextInput/generalTextInput'
import { TrashButton } from '../../../common/components/actions/trashBtn'
import ColorOptionsSelect from '../Files/Excel/ColorSelector/colorOptionsSelect'
import getHeaderInfo from '../../../api/headerInfo'
import PipesForm from '../../../common/components/PipesForm'
import LiveSpan from '../Content/LiveSpan'

import GlobalNotification from 'live/services/global-notification'
import { Notification } from '../../../common/components/Notification/Notification'
import SettingsColumns from './SettingsColumns'
import { Icon } from '../../../common/styles/global_styles'
import FollowingGlobalSpanIcon from '../Content/emailType/FollowinGlobalSpanIcon'

const eventStore = require('live/components/events/lib/store')('config/datasource-form', {
    enforceDependencies: true
})

const eventActions = require('live/components/events/lib/actions')('config/datasource-form')


interface Props {
    sheet: Sheet,
    editMode: boolean
    update: (sheet: Sheet) => void
    shouldResetStore?: boolean
    resetStoreCallback?: () => void 
}
export default function SheetForm({ sheet, editMode, update, shouldResetStore = false, resetStoreCallback = () => {} }: Props) {
    const [headerInfo, setHeaderInfo] = useState<any>()
    const [openModal, setStateModal] = useState<boolean>(false)
    const [openSettings, setOpenSettings] = useState<boolean>(editMode)
    const [queryFields, setQueryFields] = useState<string[]>([])

    useEffect(() => {
        if(shouldResetStore){
            if(eventActions && eventActions.reset && typeof eventActions.reset === 'function'){
                eventActions.reset()
                resetStoreCallback()
            }
        }
    }, [shouldResetStore])

    useEffect(() => {
        getHeaderInfo(sheet).then(({ status }) => {
            const newHeader = status[0]
            if (!!newHeader) {
                setHeaderInfo(newHeader)
            }
        })
    }, [openModal])

    useEffect(() => {
        setQueryFields([...(headerInfo?.select ?? []), ...(headerInfo?.group ?? [])])
    }, [headerInfo])


    useEffect(() => {
        if (editMode) {
            setOpenSettings(true)
        } else {
            setOpenSettings(false)
        }
    }, [editMode])
    const actions = useMemo(() => {
        return {
            ...eventActions,
            setChart: function () {
                return false
            },
            setProvider: (provider) => {
                if (provider !== 'pipes') {
                    GlobalNotification.error(
                        <Notification title={i18n(`Can't change provider`)}></Notification>, 5
                    )
                }

            }
        }
    }, [])


    const ExpressionPreview = useMemo(() => {
        return require('live/components/forms/ui/expression-preview')(eventStore, actions)
    }, [actions, eventStore])

    return <div>
        <Card>
            <label>
                Tab Name {sheet?.name}
            </label>
            {
                editMode ? <CardActions>                    
                    <TrashButton 
                        message={`${i18n('Are you sure that you want to delete')} ${sheet?.name}?`} 
                        onClick={() =>{
                            update(undefined)
                        }}
                    />
                    <Icon className={`far fa-chevron-${openSettings ? 'up' : 'down'} chevron`} onClick={() => setOpenSettings(!openSettings)} />
                </CardActions> : null
            }

        </Card>

        <SheetConfig open={openSettings}>
            <SheetConfigGeneral>
                <div className='tabName'>
                    <GeneralTextInput
                        variant={'primary'}
                        border={true}
                        title={i18n('Tab name')}
                        error={false}
                        editMode={editMode}
                        placeholder={i18n('Name of the tab')}
                        required={true}
                        value={sheet?.name}
                        onChange={name => update({ ...sheet, name })}
                    />
                </div>
                <Span>
                    <LiveSpan setSpan={(span) => update({ ...sheet, span })} span={sheet?.span} defaultSpan='today' ></LiveSpan>
                    <div className='unfollowIcon'>
                        <FollowingGlobalSpanIcon
                            follow={!sheet?.unfollowSpan}
                            onClick={() => {
                                update({ ...sheet, unfollowSpan: !sheet?.unfollowSpan })
                            }}
                        />
                    </div>
                </Span>
                <ColorOptionsSelect
                    text="Header Font Color"
                    value={sheet?.colorFont}
                    name="backgroundColor"
                    onChange={(field, value) => update({ ...sheet, colorFont: value })}
                ></ColorOptionsSelect>
                <ColorOptionsSelect
                    text="Header Background Color"
                    value={sheet?.colorHeader}
                    name="backgroundColor"
                    onChange={(field, value) => update({ ...sheet, colorHeader: value })}
                ></ColorOptionsSelect>
                <Button className='btn' onClick={() => setStateModal(true)}> <i className="fa fa-cog" /><label>Columns Configuration</label></Button>
            </SheetConfigGeneral>
            <SheetConfigExpression>
                <ExpressionPreview
                    expressionHasChanged={true}
                    readonly={!editMode}
                    expressionHasChangedTemplate={<span />}
                    data={{
                        provider: 'pipes',
                        expression: sheet?.expression
                    }}
                    apply={({ expression }) => update({ ...sheet, expression })}
                />
            </SheetConfigExpression>

            <StyledLiveModal show={openModal} onHide={() => setStateModal(false)} animation={false} className="modal-sm">
                <StyledLiveModal.Header>
                    <StyledLiveModal.Title>
                        {i18n(`Columns Configuration`)}
                    </StyledLiveModal.Title>
                </StyledLiveModal.Header>
                <StyledLiveModal.Body>
                    <SettingsColumns 
                        queryFields={queryFields} 
                        fields={(sheet?.fields ?? []).filter(field => queryFields.includes(field.name))} 
                        update={(fields) => update({ ...sheet, fields })} 
                    />

                </StyledLiveModal.Body>
                <StyledLiveModal.Footer>
                    <StyledLiveModal.ModalActions
                        onSubmit={() => {
                            setStateModal(false)
                        }}
                        submitText={i18n('Save')}
                        submitBtnClassName="btn btn-primary"
                        cancelBtnClassName={null}
                        onCancel={() => {
                            setStateModal(false)
                        }}
                        cancelText={i18n('Cancel')}
                    />
                </StyledLiveModal.Footer>
            </StyledLiveModal>
        </SheetConfig>
    </div>
}
//https://poi.apache.org/apidocs/dev/org/apache/poi/ss/usermodel/BuiltinFormats.html


const Card = styled.div`
    width: 100%;
    border-top:1px solid rgb(186, 192, 203);
    padding-left: 20px;
    height:30px;
    display: flex;
    direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;

`
const CardActions = styled.div`
    
    padding-right: 20px;
    >*{
        margin-right: 10px;
    }

    ${Icon}{
        padding-right:30px;
        padding-left:30px;
        transition: .2s;
        padding: 4px;
        border-radius: 3px;
        align-self: flex-end;                
    }    
    .chevron{
        font-weight: bold;
        color:#37A3EF;
        cursor: pointer;
    }
`
const SheetConfig = styled.div<{
    open: boolean
}>`
    height:380px;
    background: #F5F5F5;
    padding: 24px;
    display:${({ open }) => open ? 'flex' : 'none'};

   .console-body-container-preview,.CodeMirror {
       height: 265px;       
   }
`




const Button = styled.button`
    width: 100%;
    border:1px solid rgb(186, 192, 203);
    background: #F5F5F5;
    border-radius:3px;
    label{
        display: inline;
    }
`
const SheetConfigGeneral = styled.div`
  width: 240px;
  height:100%;
  & > * {
    margin: 8px 0px;
  }
  & .tabName {
    margin-top: 0px;
    .textBlock , .textBlockLabelHeader {
        margin-top: 0px
      }
  }
`
const SheetConfigExpression = styled.div`    
    width: 100%;
    padding-left: 25px;
    padding-bottom: 24px;        
`
const Span = styled.div`
width: 100%;
display: flex;
.btn-group{
    padding: 0px;
    
    div{
        width: 100%;
    }
}

.unfollowIcon {
    justify-content: center;
    align-items: center;
    align-self: center;
    margin: 10px 0px 0px 10px;
    padding-top: 5px;
}
`

const StyledLiveModal = styled(LiveModal)`
    .modal-dialog {
        width: 950px;
    }
    .modal-body {
        //height: 500px;
        //max-height: 900px;
    }
`
